import type { VisitFrequencyData, OverdueStatus } from "../types";
import { cn } from "../lib/utils";
import { formatShortDate } from "../../utils/dateFormatters";

interface VisitFrequencyIndicatorProps {
  visitFrequency: VisitFrequencyData | null;
  className?: string;
}

/**
 * Determine overdue status by comparing days since last visit
 * to the average visit cadence.
 *
 * - on-track: within 80% of avg cadence
 * - approaching: between 80% and 120%
 * - overdue: beyond 120%
 */
function getOverdueStatus(vf: VisitFrequencyData): OverdueStatus {
  if (!vf.avg_days_between_visits || !vf.last_visit_date) return "on-track";

  const lastVisit = new Date(vf.last_visit_date);
  const daysSince = Math.floor(
    (Date.now() - lastVisit.getTime()) / (1000 * 60 * 60 * 24),
  );
  const avg = vf.avg_days_between_visits;

  if (daysSince <= avg * 0.8) return "on-track";
  if (daysSince <= avg * 1.2) return "approaching";
  return "overdue";
}

const STATUS_STYLES: Record<OverdueStatus, string> = {
  "on-track": "text-emerald-600 dark:text-emerald-400",
  approaching: "text-amber-600 dark:text-amber-400",
  overdue: "text-accent-500 dark:text-accent-400",
};

const STATUS_DOT: Record<OverdueStatus, string> = {
  "on-track": "bg-emerald-500",
  approaching: "bg-amber-500",
  overdue: "bg-accent-500",
};

/**
 * Parse a date string, treating date-only "YYYY-MM-DD" as local midnight
 * instead of UTC midnight (which would display as the previous day in US timezones).
 */
function parseDateLocal(dateStr: string): Date {
  if (dateStr.length === 10 && !dateStr.includes("T")) {
    return new Date(dateStr + "T00:00:00");
  }
  return new Date(dateStr);
}

/**
 * Calculate days ago from a date string.
 */
function daysAgo(dateStr: string): number {
  const d = parseDateLocal(dateStr);
  return Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
}

const STATUS_LABELS: Record<OverdueStatus, string> = {
  "on-track": "On track",
  approaching: "Visit due soon",
  overdue: "Visit overdue",
};

export function VisitFrequencyIndicator({
  visitFrequency,
  className,
}: VisitFrequencyIndicatorProps) {
  if (!visitFrequency || visitFrequency.total_visits === 0) {
    return (
      <p className={cn("text-sm text-text-muted", className)}>
        No visit history
      </p>
    );
  }

  const status = getOverdueStatus(visitFrequency);
  const avgDays = visitFrequency.avg_days_between_visits;
  const lastDate = visitFrequency.last_visit_date;
  const daysSinceVisit = lastDate ? daysAgo(lastDate) : null;

  return (
    <div className={cn("flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm", className)}>
      {/* Status label with dot */}
      {avgDays != null && (
        <span className={cn("flex items-center gap-1.5", STATUS_STYLES[status])}>
          <span className={cn("inline-block h-1.5 w-1.5 rounded-full", STATUS_DOT[status])} />
          {status === "overdue" && daysSinceVisit != null
            ? `Visit overdue \u2014 ${daysSinceVisit}d since last visit`
            : `${STATUS_LABELS[status]} \u00b7 every ~${Math.round(avgDays)}d`}
        </span>
      )}

      {/* Last visited (shown separately when not overdue) */}
      {status !== "overdue" && lastDate && (
        <>
          {avgDays != null && (
            <span className="text-text-muted" aria-hidden>&middot;</span>
          )}
          <span className="text-text-secondary">
            Last visited {formatShortDate(lastDate)}
            <span className="text-text-muted"> ({daysSinceVisit}d ago)</span>
          </span>
        </>
      )}

      {/* For overdue, show the cadence as secondary info */}
      {status === "overdue" && avgDays != null && lastDate && (
        <span className="text-text-muted">
          (expected every ~{Math.round(avgDays)}d)
        </span>
      )}
    </div>
  );
}
